import { HttpClient, HttpContext, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { FrequentlyUsedFieldsResponse } from '@app/v2/personas/manage-persona/data/form-field-library.types'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { environment } from '@environments/environment'
import { Observable } from 'rxjs'
import { personaDetailResponse, personaPatchResponse, personaRegistrationCountResponse, personaResponse } from '../types/persona.type'

@Injectable()
export class PersonaService {
  constructor(private _http: HttpClient) {}

  /**
   * Get all personas for a given event
   * @param {number} organizationId - The ID of the workspace
   * @param {number} eventId - The ID of the event
   * @returns {Observable<personaResponse>} - An observable of the response from the API
   */
  getPersonas(organizationId: number, eventId: number, perPage: number = 10, page: number = 1): Observable<personaResponse> {
    const params = new HttpParams().set('per_page', perPage).set('page', page)
    return this._http.get<personaResponse>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/personas`, {
      params,
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /** Persona detail, including the registration form and its fields when the API provides them. */
  getPersona(organizationId: number, eventId: number, personaId: number): Observable<personaDetailResponse> {
    return this._http.get<personaDetailResponse>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/personas/${personaId}/`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Update the name of a persona for a given event
   * @param {number} organizationId - The ID of the workspace
   * @param {number} eventId - The ID of the event
   * @param {number} personaId - The ID of the persona
   * @param {string} name - The new name of the persona
   * @returns {Observable<personaPatchResponse>} - An observable of the response from the API
   */
  updatePersonaName(organizationId: number, eventId: number, personaId: number, name: string): Observable<personaPatchResponse> {
    return this._http.patch<personaPatchResponse>(
      `${environment.planningApi}organizations/${organizationId}/events/${eventId}/personas/${personaId}`,
      { name },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  /*
   * Create a new persona for a given event
   * @param {number} organizationId - The ID of the workspace
   * @param {number} eventId - The ID of the event
   * @param {string} name - The name of the persona
   * @returns {Observable<personaResponse>} - An observable of the response from the API
   */
  createPersona(organizationId: number, eventId: number, name: string): Observable<personaResponse> {
    return this._http.post<personaResponse>(
      `${environment.planningApi}organizations/${organizationId}/events/${eventId}/personas`,
      { name },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  /**
   * Clone a persona for a given event with an explicit name and slug.
   * @param {number} organizationId - The ID of the workspace
   * @param {number} eventId - The ID of the event
   * @param {number} personaId - The ID of the persona
   * @param {{ name: string; slug: string }} payload - Required clone name and URL slug
   * @returns {Observable<personaPatchResponse>} - An observable of the response from the API
   */
  clonePersona(organizationId: number, eventId: number, personaId: number, payload: { name: string; slug: string }): Observable<personaPatchResponse> {
    return this._http.post<personaPatchResponse>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/personas/${personaId}/clone`, payload, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Prefetch workspace frequently-used field type ids for the form field library.
   * @param {number} organizationId - Workspace id
   */
  getFrequentlyUsedFields(organizationId: number): Observable<FrequentlyUsedFieldsResponse> {
    return this._http.get<FrequentlyUsedFieldsResponse>(`${environment.planningApi}organizations/${organizationId}/frequently-used-fields`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }
}
